/**
 * The berth asset list (M8 plan MC-b6b): every berth the pilot holds, read from anywhere: the dashboard's
 * **Fleet › Berths** section (dashboard spec §2.2, W3 card M2; `features/berthAssets/feature.ts`). Read-only: storing
 * and taking stay on a station's Berth tab (MC-b6a). Thin: the rows and totals come from `ui/models/berthAssets.ts`.
 *
 * The section (`createAssetListSection`) re-renders whenever its source says the input may have changed; the
 * dashboard shell owns the frame, Close, the hints, the modal and the keys. UI events only.
 */
import type { ReactElement, ReactNode } from 'react'
import type { DashSectionComponent } from '../dashboard/registry'
import { useLive } from '../fleet/sectionHost'
import { berthAssetsModel, noCareerAssetsModel, type BerthAssetsInput, type BerthAssetsModel } from '../models/berthAssets'
import '../strategic/strategic.css'
import '../fleet/stationPanels.css'

export interface AssetListProps {
  model: BerthAssetsModel
}

export function AssetList(p: AssetListProps): ReactNode {
  const m = p.model
  return (
    <div className="sui fp-panel" data-testid="berth-assets" role="region" aria-label={m.title}>
      <p className="sui-sub">{m.sub}</p>
      {m.totalsLabel !== '' ? (
        <p className="fp-meta" data-testid="berth-assets-totals">
          {m.totalsLabel}
        </p>
      ) : null}
      {m.empty !== null ? <p className="sui-empty">{m.empty}</p> : null}
      {m.rows.map((r) => (
        <section key={r.stationId} className="fp-row" data-testid={`berth-assets-row-${r.stationId}`} data-access={r.access} data-here={r.here ? '' : undefined}>
          <div>
            <div className="fp-row__name">{r.name}</div>
            <div className="fp-row__reason" data-testid={`berth-assets-state-${r.stationId}`}>
              {r.stateLabel}
            </div>
          </div>
          <div className="fp-row__price ui-num">{r.totalsLabel}</div>
          <ul className="sui-list">
            {r.items.map((i) => (
              <li key={i.key}>
                {i.name} <span className="ui-num">{i.amountLabel}</span>
              </li>
            ))}
          </ul>
          <p className="sui-sub">{r.note}</p>
        </section>
      ))}
    </div>
  )
}

/** What the section reads (`features/berthAssets/feature.ts` builds it from the career runtime and the strategic client). */
export interface AssetListSource {
  /** The model input, or null while no career runs. */
  input(): BerthAssetsInput | null
  /** Calls `fn` whenever the input may have changed; returns the unsubscribe. */
  subscribe(fn: () => void): () => void
}

/** The Fleet › Berths section body (read-only; no buttons, so no focus inputs). */
export function createAssetListSection(source: AssetListSource): DashSectionComponent {
  const subscribe = (fn: () => void): (() => void) => source.subscribe(fn)
  return function BerthsSection(): ReactElement {
    useLive(subscribe)
    const input = source.input()
    return <AssetList model={input === null ? noCareerAssetsModel() : berthAssetsModel(input)} />
  }
}
